import {
  MOBILE_BREAKPOINT,
  tokens,
  Typography,
  useSingletonMobileMediaQuery,
  useSingletonTabletMediaQuery,
  Button,
} from "@sunrun/experience-ui-components";
import { useState } from "react";
import styled from "@emotion/styled/macro";
import {
  ReviewCashSummary,
  ReviewFinancialBreakdown,
  ReviewLoanSummary,
  ReviewMonthlySummary,
  ReviewFlexMonthlySummary,
  ReviewPrepaidSummary,
  ReviewOfferProduct,
  ReviewSummaryBase,
} from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { OfferReviewPromotionPills } from "./OfferReviewPromotionPills";
import {
  RowContainer,
  RowContainerWithTooltip,
} from "components/atoms/PriceGlobals";
import {
  StyledCategory,
  StyledFootnote,
  StyledHeader,
} from "components/atoms/GlobalStyles";
import { CardSectionDivider } from "components/atoms/Card";
import {
  formatCurrency,
  formatPercent,
  formatProductMinMaxCost,
} from "utils/formatHelpers";
import { sortProductsByDisplayOrder } from "utils/sortProducts";
import {
  FLEX_MONTHLY,
  MONTHLY,
  type FinancialProduct,
} from "constants/financialProducts";
import {
  PRODUCT_DISPLAY_ORDER,
  SOLAR_PRODUCT_ID,
  POWER_MANAGEMENT_PRODUCT_ID,
} from "constants/bundleAndProductIds";
import {
  COSTCO_CARD_ESTIMATE,
  ESTIMATED_NET_SYSTEM_COST,
  LOAN_APR,
  POST_SOLAR_UTILITY_BILL,
  YEAR_1_ENERGY_RATE,
} from "constants/pricing-tooltips";
import { OfferMessages } from "components/molecules/OfferMessages";
import { OfferReviewRebatesAndIncentives } from "components/molecules/OfferReviewRebatesAndIncentives";
import { NEW_HOMES_MONTHLY_AUTOPAY_DISCOUNT_TEXT } from "constants/newHomes";
import { type PromotionSelectionItem } from "services/promotions";

const BACKUP_STORAGE_BATTERY_IDS = new Set<string>(
  PRODUCT_DISPLAY_ORDER.BATTERIES
);

const OFFER_REVIEW_FLEX_MAX_HIDDEN_STATES = new Set(["TX", "MA", "HI"]);

const shouldHideFlexMaxOnOfferReview = (state: string) =>
  OFFER_REVIEW_FLEX_MAX_HIDDEN_STATES.has(state?.toUpperCase());

const isMonthlyProductPayment = (
  paymentType: ReviewOfferProduct["paymentType"]
) => paymentType === MONTHLY || paymentType === FLEX_MONTHLY;

const isMonthlyEquipmentLineItem = (p: ReviewOfferProduct) => {
  if (!isMonthlyProductPayment(p.paymentType) || Number(p.cost) <= 0) {
    return false;
  }
  return (
    p.productId === SOLAR_PRODUCT_ID ||
    p.productId === POWER_MANAGEMENT_PRODUCT_ID ||
    BACKUP_STORAGE_BATTERY_IDS.has(p.productId)
  );
};

const categoryTitleForMonthlyEquipment = (p: ReviewOfferProduct): string => {
  if (BACKUP_STORAGE_BATTERY_IDS.has(p.productId)) return "Storage";
  if (p.productId === POWER_MANAGEMENT_PRODUCT_ID) return "Span";
  return p.name;
};

const labelForMonthlyEquipmentNestedLine = (p: ReviewOfferProduct) =>
  `${categoryTitleForMonthlyEquipment(p)} (${p.count})`;

const getEvChargerDueAtInstallationRows = (summary: ReviewSummaryBase) =>
  summary.evChargerDueAtInstallation ?? [];

const EvChargerDueAtInstallationRows = ({
  summary,
  decimalPlaces = 0,
}: {
  summary: ReviewSummaryBase;
  decimalPlaces?: number;
}) => {
  const rows = getEvChargerDueAtInstallationRows(summary);
  if (rows.length === 0) {
    return null;
  }

  return (
    <>
      {rows.map((row) => (
        <RowContainer
          key={row.productId}
          label={`${row.label}: Due at Installation`}
          cost={formatCurrency(row.value, decimalPlaces)}
          accentColor={tokens.BRAND_HERO_BLUE}
        />
      ))}
    </>
  );
};

const MonthlyPricedEquipmentLineItems = ({
  products,
}: {
  products: ReviewOfferProduct[];
}) => {
  const items = sortProductsByDisplayOrder(products).filter(
    isMonthlyEquipmentLineItem
  );

  const [expanded, setExpanded] = useState(true);
  if (items.length === 0) return null;

  if (!expanded) {
    return (
      <StyledButton color="tertiary" onClick={() => setExpanded(true)}>
        View payment details
      </StyledButton>
    );
  }

  return (
    <>
      <MonthlyEquipmentNestedList>
        {items.map((p, index) => {
          const paymentType = p.paymentType as FinancialProduct;
          const min = p.minimumCost != null ? Number(p.minimumCost) : null;
          const max = Number(p.cost);
          const hasRange = min != null && min > 0 && min !== max;
          const priceText = hasRange
            ? formatProductMinMaxCost(p.minimumCost, p.cost, paymentType)
            : `${formatCurrency(max, 0)}/mo`;

          return (
            <NestedEquipmentRow key={`${p.productId}-${index}`}>
              <NestedEquipmentLabel>
                {labelForMonthlyEquipmentNestedLine(p)}
              </NestedEquipmentLabel>
              <NestedEquipmentPrice>{priceText}</NestedEquipmentPrice>
            </NestedEquipmentRow>
          );
        })}
      </MonthlyEquipmentNestedList>
      <StyledButton color="tertiary" onClick={() => setExpanded(false)}>
        Hide payment details
      </StyledButton>
    </>
  );
};

const StyledButton = styled(Button)`
  text-decoration: underline;
  color: ${tokens.BRAND_HERO_BLUE};
  padding: 0;
  margin: 0;
`;

const isFiniteAmount = (value: number | undefined): value is number =>
  Number.isFinite(value);

type SecondSystemUtilitySummary = ReviewSummaryBase & {
  averageEstimatedCurrentUtilityBill: number;
  postSolarUtility: number;
};

const hasSecondSystemUtilityBillComparison = (
  summary: ReviewSummaryBase,
  isAdditionalSystem: boolean
): summary is SecondSystemUtilitySummary =>
  isAdditionalSystem &&
  isFiniteAmount(summary.averageEstimatedCurrentUtilityBill) &&
  isFiniteAmount(summary.postSolarUtility);

const hasSecondSystemMonthlyPayments = (
  summary: ReviewMonthlySummary,
  isAdditionalSystem: boolean
): summary is ReviewMonthlySummary & SecondSystemUtilitySummary =>
  hasSecondSystemUtilityBillComparison(summary, isAdditionalSystem) &&
  isFiniteAmount(summary.firstYearMonthly);

const CurrentSunrunPaymentRow = ({ cost }: { cost: number }) => (
  <RowContainer
    label="Current Sunrun payment"
    cost={formatCurrency(cost)}
    unit="/mo"
    accentColor={tokens.BRAND_HERO_BLUE}
  />
);

const SecondSystemWasNowComparison = ({
  currentUtilityBill,
  newSystemUtilityBill,
  currentSunrunPayment,
  additionalYear1Payment,
  isMobile,
  state,
}: {
  currentUtilityBill: number;
  newSystemUtilityBill: number;
  currentSunrunPayment?: number;
  additionalYear1Payment?: number;
  isMobile: boolean;
  state: string;
}) => {
  const showCurrentSunrunPayment = isFiniteAmount(currentSunrunPayment);

  return (
    <>
      {state !== "TX" && (
        <>
          <DividerRow>
            <HelperText
              size={14}
              fontStyle="italic"
              color={tokens.BRAND_HERO_BLUE}
              isMobile={isMobile}
            >
              Was
            </HelperText>
            <InvisibleDivider />
          </DividerRow>
          <RowContainer
            label="Avg. est. current utility bill"
            cost={currentUtilityBill}
            unit="/mo"
            accentColor={tokens.BRAND_HERO_BLUE}
          />
          {showCurrentSunrunPayment && (
            <CurrentSunrunPaymentRow cost={currentSunrunPayment} />
          )}
          <DividerRow>
            <HelperText
              size={14}
              fontStyle="italic"
              color={tokens.BRAND_HERO_BLUE}
              isMobile={isMobile}
            >
              Now
            </HelperText>
            <FlexDivider />
          </DividerRow>
        </>
      )}
      <RowContainerWithTooltip
        tooltip={POST_SOLAR_UTILITY_BILL}
        label="Avg. est. utility bill with new Sunrun system"
        cost={newSystemUtilityBill}
        unit="/mo"
        accentColor={tokens.BRAND_HERO_BLUE}
      />
      {showCurrentSunrunPayment && (
        <CurrentSunrunPaymentRow cost={currentSunrunPayment} />
      )}
      {additionalYear1Payment != null && (
        <RowContainer
          label="Additional Year 1 total Sunrun payment"
          cost={formatCurrency(additionalYear1Payment)}
          unit="/mo"
          accentColor={tokens.BRAND_HERO_BLUE}
        />
      )}
    </>
  );
};

type OfferReviewPriceDetailsProps = {
  financialBreakdown: ReviewFinancialBreakdown;
  consumption: number;
  production: number;
  hasBattery?: boolean;
  nonSolarView?: boolean;
  isAdditionalSystem?: boolean;
  singleColumn?: boolean;
  products?: ReviewOfferProduct[];
  isNewHomes?: boolean;
  promotions?: PromotionSelectionItem[];
};

type NonSolarPaymentDetailsProps = Omit<
  OfferReviewPriceDetailsProps,
  "nonSolarView" | "singleColumn"
>;

const AgreementDetailsAndMonthlyPayments = ({
  financialBreakdown,
  consumption,
  production,
  hasBattery = false,
  nonSolarView = false,
  isAdditionalSystem = false,
  singleColumn = false,
  products = [],
  isNewHomes = false,
  promotions,
}: OfferReviewPriceDetailsProps) => {
  const isMobile = useSingletonMobileMediaQuery();
  const isTablet = useSingletonTabletMediaQuery();
  const hideNonSolarSecondSystemComparison = nonSolarView && isAdditionalSystem;
  const summary = financialBreakdown.summary;
  const summaryType = summary?.type;
  const monthlyAutopayDiscountUnderline = isNewHomes
    ? NEW_HOMES_MONTHLY_AUTOPAY_DISCOUNT_TEXT
    : "Includes 5% ACH discount";

  const renderAgreementCategory = () => {
    if (!summary) return null;

    switch (summaryType) {
      case "Monthly": {
        const s = summary as ReviewMonthlySummary;
        return (
          <CategoryNoMargin>
            {!singleColumn && (
              <StyledHeader>
                <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                  Agreement details
                </Typography>
              </StyledHeader>
            )}
            <RowContainer
              underline={monthlyAutopayDiscountUnderline}
              bold
              label={`Total Monthly Payment${
                s.annualEscalator > 0 ? " in the first year" : ""
              }`}
              cost={formatCurrency(s.firstYearMonthly)}
              unit="/mo"
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            {!nonSolarView && (
              <RowContainer
                label="Year 1 Energy Rate"
                cost={s.year1RateKWh}
                decimals={3}
                unit="/kWh"
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            )}
            <RowContainer
              label="Annual Escalator on Monthly Payment"
              cost={`${formatPercent(s.annualEscalator)}`}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainer
              label="Agreement Term"
              cost={s.termYears.toString()}
              unit="Years"
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainer
              label="Due at signing"
              cost={formatCurrency(summary.dueAtSigning)}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <EvChargerDueAtInstallationRows summary={summary} />
          </CategoryNoMargin>
        );
      }
      case "FlexMonthly": {
        const s = summary as ReviewFlexMonthlySummary;
        const marginalRateValue = s.rates.find(
          (r) => r.kind === "Marginal"
        )?.value;
        const marginalRate = marginalRateValue
          ? Number.parseFloat(marginalRateValue)
          : null;
        const consumptionRate = Number.parseFloat(
          s.rates.find((r) => r.kind === "Consumption")?.value ?? "0"
        );
        const productionRate = Number.parseFloat(
          s.rates.find((r) => r.kind === "Production")?.value ?? "0"
        );
        const formattedFlexRate = formatCurrency(
          consumptionRate - productionRate,
          3
        );
        const formattedDelta = (production - consumption).toLocaleString();
        const oneYearFlexRateTooltip = !shouldHideFlexMaxOnOfferReview(
          summary.state
        )
          ? `Flex has additional discounted electricity ready for your needs, and you only pay for what you use. Your maximum monthly bill would be ${formatCurrency(
              s.maximumMonthlyPayment,
              2
            )}, if you used the additional ${formattedDelta} kWh available.`
          : `Flex has additional discounted electricity ready for your needs, and you only pay for what you use. Your maximum monthly bill will be based on the system's monthly energy production.`;
        return (
          <CategoryNoMargin>
            {!singleColumn && (
              <StyledHeader>
                <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                  Agreement details
                </Typography>
              </StyledHeader>
            )}
            <RowContainerWithTooltip
              underline={monthlyAutopayDiscountUnderline}
              medium
              label="Minimum monthly payment in the first year"
              tooltip="An included component of your Minimum Monthly Bill is a production charge applied to Total Estimated Production."
              cost={formatCurrency(s.minimumMonthlyPayment, 2)}
              unit="/mo"
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainerWithTooltip
              label="Year 1 Flex Rate for additional energy"
              description={`For up to an additional ${formattedDelta} annual kWh consumed`}
              cost={marginalRate ?? formattedFlexRate}
              decimals={3}
              unit="/kWh"
              tooltip={oneYearFlexRateTooltip}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainerWithTooltip
              label="Annual Escalator"
              cost={`${formatPercent(s.annualEscalator)}`}
              unit="Yearly"
              tooltip="See your agreement for more information about how your escalator/s apply to your minimum bill and Flex Rate for additional energy"
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainer
              label="Agreement Term"
              cost={s.termYears.toString()}
              unit="Years"
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainer
              label="Due at signing"
              cost={formatCurrency(summary.dueAtSigning, 2)}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <EvChargerDueAtInstallationRows
              summary={summary}
              decimalPlaces={2}
            />
          </CategoryNoMargin>
        );
      }
      case "Loan": {
        const s = summary as ReviewLoanSummary;
        return (
          <CategoryNoMargin>
            {!singleColumn && (
              <StyledHeader>
                <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                  Agreement details
                </Typography>
              </StyledHeader>
            )}
            <RowContainer
              medium
              label="Loan Payment"
              cost={formatCurrency(s.monthlyWithVoluntary)}
              unit="/mo"
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainer
              label="Total Loan Amount"
              cost={s.total}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainerWithTooltip
              tooltip={LOAN_APR}
              label="APR includes 0.50% Autopay Discount"
              cost={`${s.apr}%`}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainer
              label="Loan Term"
              cost={`${s.termYears} years`}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainer
              label="Due at signing"
              cost={formatCurrency(summary.dueAtSigning)}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <EvChargerDueAtInstallationRows summary={summary} />
          </CategoryNoMargin>
        );
      }
      case "Prepaid": {
        const s = summary as ReviewPrepaidSummary;
        return (
          <CategoryNoMargin>
            {!singleColumn && (
              <StyledHeader>
                <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                  Agreement details
                </Typography>
              </StyledHeader>
            )}
            <RowContainer
              medium
              label="Total Amount"
              cost={formatCurrency(s.total, 0)}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainerWithTooltip
              tooltip={YEAR_1_ENERGY_RATE}
              label="Year 1 Energy Rate"
              cost={s.year1RateKWh}
              decimals={3}
              unit="/kWh"
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainer
              label="Agreement Term"
              cost={s.termYears.toString()}
              unit="Years"
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainer
              label="Due at signing"
              cost={formatCurrency(summary.dueAtSigning)}
              unit="deposit"
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainer
              label="Due at Installation"
              cost={formatCurrency(s.dueAtInstallation)}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <EvChargerDueAtInstallationRows summary={summary} />
          </CategoryNoMargin>
        );
      }
      case "Cash": {
        const s = summary as ReviewCashSummary;
        return (
          <CategoryNoMargin>
            {!singleColumn && (
              <StyledHeader>
                <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                  Agreement details
                </Typography>
              </StyledHeader>
            )}
            {nonSolarView ? (
              <RowContainer
                medium
                label="Total system cost"
                cost={formatCurrency(s.total)}
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            ) : (
              <RowContainerWithTooltip
                medium
                label="Est. Net System Cost"
                tooltip={ESTIMATED_NET_SYSTEM_COST}
                cost={formatCurrency(s.netCost)}
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            )}
            {!nonSolarView && (
              <RowContainerWithTooltip
                tooltip={YEAR_1_ENERGY_RATE}
                label="Year 1 Energy Rate"
                cost={s.year1RateKWh}
                decimals={3}
                unit="/kWh"
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            )}
            <RowContainer
              label="Total System Cost"
              cost={s.total}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            {!nonSolarView && (
              <RowContainer
                label="Total System Cost (before tax)"
                cost={s.total}
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            )}
            <RowContainer
              label="Due at signing"
              cost={formatCurrency(summary.dueAtSigning)}
              unit="deposit"
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <RowContainer
              label="Due at Installation"
              cost={formatCurrency(s.dueAtInstallation)}
              accentColor={tokens.BRAND_HERO_BLUE}
            />
            <EvChargerDueAtInstallationRows summary={summary} />
          </CategoryNoMargin>
        );
      }
      default:
        return null;
    }
  };

  const renderAgreementDetails = () => {
    if (!summary) return null;
    const agreementCategory = renderAgreementCategory();
    if (!agreementCategory) return null;
    return (
      <>
        {agreementCategory}
        <OfferReviewRebatesAndIncentives
          summary={summary}
          singleColumn={singleColumn}
          nonSolarView={nonSolarView}
        />
      </>
    );
  };

  const renderMonthlyPayments = () => {
    if (!summary) return null;
    switch (summaryType) {
      case "Monthly": {
        const s = summary as ReviewMonthlySummary;
        if (hasSecondSystemMonthlyPayments(s, isAdditionalSystem)) {
          return (
            <CategoryNoMargin>
              {!singleColumn && (
                <StyledHeader>
                  <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                    Monthly payments
                  </Typography>
                </StyledHeader>
              )}
              {!hideNonSolarSecondSystemComparison && (
                <SecondSystemWasNowComparison
                  currentUtilityBill={s.averageEstimatedCurrentUtilityBill}
                  newSystemUtilityBill={s.postSolarUtility}
                  currentSunrunPayment={s.currentSunrunPayment}
                  additionalYear1Payment={s.firstYearMonthly}
                  isMobile={isMobile}
                  state={s.state}
                />
              )}
              <MonthlyPricedEquipmentLineItems products={products} />
            </CategoryNoMargin>
          );
        }
        return (
          <CategoryNoMargin>
            {!singleColumn && (
              <StyledHeader>
                <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                  Monthly payments
                </Typography>
              </StyledHeader>
            )}
            {s.market === "Y1_MONTHLY_PAYMENTS" &&
              s.state !== "TX" &&
              !hideNonSolarSecondSystemComparison && (
                <>
                  <DividerRow>
                    <HelperText
                      size={14}
                      fontStyle="italic"
                      color={tokens.BRAND_HERO_BLUE}
                      isMobile={isMobile}
                    >
                      Was
                    </HelperText>
                    <InvisibleDivider />
                  </DividerRow>
                  <RowContainer
                    label="Estimated Monthly Avg. Pre-Solar Utility Bill"
                    cost={s.preSolarUtility}
                    unit="/mo"
                    accentColor={tokens.BRAND_HERO_BLUE}
                  />
                  <DividerRow>
                    <HelperText
                      size={14}
                      fontStyle="italic"
                      color={tokens.BRAND_HERO_BLUE}
                      isMobile={isMobile}
                    >
                      Now
                    </HelperText>
                    <FlexDivider />
                  </DividerRow>
                </>
              )}
            {!(s.market === "CONTROL" && s.state !== "TX") &&
              !hideNonSolarSecondSystemComparison && (
                <RowContainerWithTooltip
                  tooltip={POST_SOLAR_UTILITY_BILL}
                  label="Avg. Est. Post-Solar Utility Bill"
                  cost={s.postSolarUtility}
                  unit="/mo"
                  accentColor={tokens.BRAND_HERO_BLUE}
                />
              )}
            {s.market === "CONTROL" && s.state !== "TX" ? (
              <RowContainerWithTooltip
                label="Total Monthly Sunrun Bill"
                tooltip={POST_SOLAR_UTILITY_BILL}
                cost={formatCurrency(s.firstYearMonthly)}
                unit="/mo"
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            ) : (
              <RowContainer
                label="Total Monthly Sunrun Bill"
                cost={formatCurrency(s.firstYearMonthly)}
                unit="/mo"
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            )}
            <MonthlyPricedEquipmentLineItems products={products} />
          </CategoryNoMargin>
        );
      }
      case "FlexMonthly": {
        const s = summary as ReviewFlexMonthlySummary;
        const showSecondSystemUtilityBills =
          hasSecondSystemUtilityBillComparison(s, isAdditionalSystem) &&
          !hideNonSolarSecondSystemComparison;
        const formattedMin = formatCurrency(s.minimumMonthlyPayment, 2);
        const formattedMax = formatCurrency(s.maximumMonthlyPayment, 2);
        const formattedDelta = (production - consumption).toLocaleString();
        return (
          <CategoryNoMargin>
            {!singleColumn && (
              <StyledHeader>
                <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                  Monthly payments
                </Typography>
              </StyledHeader>
            )}
            {showSecondSystemUtilityBills && (
              <SecondSystemWasNowComparison
                currentUtilityBill={s.averageEstimatedCurrentUtilityBill}
                newSystemUtilityBill={s.postSolarUtility}
                currentSunrunPayment={s.currentSunrunPayment}
                isMobile={isMobile}
                state={s.state}
              />
            )}
            {!showSecondSystemUtilityBills &&
              s.market === "Y1_MONTHLY_PAYMENTS" &&
              s.state !== "TX" &&
              !hideNonSolarSecondSystemComparison && (
                <>
                  <DividerRow>
                    <HelperText
                      size={14}
                      fontStyle="italic"
                      color={tokens.BRAND_HERO_BLUE}
                      isMobile={isMobile}
                    >
                      Was
                    </HelperText>
                    <InvisibleDivider />
                  </DividerRow>
                  <RowContainer
                    label="Estimated Monthly Avg. Pre-Solar Utility Bill"
                    cost={s.preSolarUtility}
                    unit="/mo"
                    accentColor={tokens.BRAND_HERO_BLUE}
                  />
                  <DividerRow>
                    <HelperText
                      size={14}
                      fontStyle="italic"
                      color={tokens.BRAND_HERO_BLUE}
                      isMobile={isMobile}
                    >
                      Now
                    </HelperText>
                    <FlexDivider />
                  </DividerRow>
                </>
              )}
            {!showSecondSystemUtilityBills &&
              !hideNonSolarSecondSystemComparison && (
                <RowContainerWithTooltip
                  tooltip={POST_SOLAR_UTILITY_BILL}
                  label="Estimated Monthly Avg. Post-Solar Utility Bill"
                  cost={
                    s.market === "CONTROL" && s.state !== "TX"
                      ? "*"
                      : s.postSolarUtility
                  }
                  unit={s.market === "CONTROL" && s.state !== "TX" ? "" : "/mo"}
                  accentColor={tokens.BRAND_HERO_BLUE}
                />
              )}
            {!shouldHideFlexMaxOnOfferReview(s.state) && (
              <RowContainerWithTooltip
                label="Sunrun Minimum Monthly Bill"
                cost={formattedMin}
                maxCost={formattedMax}
                tooltip={`Comparison shows your Monthly Minimum bill of ${formattedMin}. If you used all ${formattedDelta} kilowatt hours of additional discounted electricity available, your maximum monthly bill in year one would be ${formattedMax}. Prices include the ACH autopay discount`}
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            )}
            {shouldHideFlexMaxOnOfferReview(s.state) && (
              <RowContainer
                label="Sunrun Minimum Monthly Bill"
                cost={formattedMin}
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            )}
            <MonthlyPricedEquipmentLineItems products={products} />
          </CategoryNoMargin>
        );
      }
      case "Loan": {
        const s = summary as ReviewLoanSummary;
        const showSecondSystemUtilityBills =
          hasSecondSystemUtilityBillComparison(s, isAdditionalSystem) &&
          !nonSolarView;
        const hasNoMonthlyBuydown =
          s.monthlyWithVoluntary === s.monthlyWithoutVoluntary;
        return (
          <CategoryNoMargin>
            {!singleColumn && (
              <StyledHeader>
                <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                  Monthly payments
                </Typography>
              </StyledHeader>
            )}
            {showSecondSystemUtilityBills && (
              <SecondSystemWasNowComparison
                currentUtilityBill={s.averageEstimatedCurrentUtilityBill}
                newSystemUtilityBill={s.postSolarUtility}
                currentSunrunPayment={s.currentSunrunPayment}
                isMobile={isMobile}
                state={s.state}
              />
            )}
            {!showSecondSystemUtilityBills &&
              s.market === "Y1_MONTHLY_PAYMENTS" &&
              !nonSolarView && (
                <>
                  <DividerRow>
                    <HelperText
                      size={14}
                      fontStyle="italic"
                      color={tokens.BRAND_HERO_BLUE}
                      isMobile={isMobile}
                    >
                      Was
                    </HelperText>
                    <InvisibleDivider />
                  </DividerRow>
                  <RowContainer
                    label="Estimated Monthly Avg. Pre-Solar Utility Bill"
                    cost={s.preSolarUtility}
                    unit="/mo"
                    accentColor={tokens.BRAND_HERO_BLUE}
                  />
                  <DividerRow>
                    <HelperText
                      size={14}
                      fontStyle="italic"
                      color={tokens.BRAND_HERO_BLUE}
                      isMobile={isMobile}
                    >
                      Now
                    </HelperText>
                    <FlexDivider />
                  </DividerRow>
                </>
              )}
            {!showSecondSystemUtilityBills && !nonSolarView && (
              <RowContainerWithTooltip
                tooltip={POST_SOLAR_UTILITY_BILL}
                label="Avg. Est. Post-Solar Utility Bill"
                cost={
                  s.market === "CONTROL" && s.state !== "TX"
                    ? "*"
                    : s.postSolarUtility
                }
                unit={s.market === "CONTROL" && s.state !== "TX" ? "" : "/mo"}
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            )}
            {!nonSolarView && (
              <RowContainer
                label="Total Monthly Sunrun Bill"
                cost={formatCurrency(s.monthlyWithVoluntary)}
                unit="/mo"
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            )}
            <MonthlyPricedEquipmentLineItems products={products} />
            {hasNoMonthlyBuydown && (
              <RowContainer
                label="Loan Payment"
                cost={formatCurrency(s.monthlyWithVoluntary)}
                unit="/mo"
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            )}
            {!hasNoMonthlyBuydown && (
              <>
                <RowContainerWithTooltip
                  tooltip={`The fixed Annual Percentage Rate for a ${s.termYears} year loan is ${s.apr}%. The amount of your first 17 payments will be $${s.monthlyWithVoluntary}. If you make a 30% voluntary payment in month 18, your remaining payments will remain approximately $${s.monthlyWithVoluntary}. If no voluntary payment is made, the remaining payments in month 18 and beyond will be $${s.monthlyWithoutVoluntary}.`}
                  label="Loan Payments 1-17 and 18+ with voluntary payment"
                  cost={s.monthlyWithVoluntary}
                  unit="/mo"
                  accentColor={tokens.BRAND_HERO_BLUE}
                />
                <RowContainerWithTooltip
                  tooltip={`The fixed Annual Percentage Rate for a ${s.termYears} year loan is ${s.apr}%. The amount of your first 17 payments will be $${s.monthlyWithVoluntary}. If you make a 30% voluntary payment in month 18, your remaining payments will remain approximately $${s.monthlyWithVoluntary}. If no voluntary payment is made, the remaining payments in month 18 and beyond will be $${s.monthlyWithoutVoluntary}.`}
                  label="Loan Payments 18+ without voluntary payment"
                  cost={s.monthlyWithoutVoluntary}
                  unit="/mo"
                  accentColor={tokens.BRAND_HERO_BLUE}
                />
              </>
            )}
          </CategoryNoMargin>
        );
      }
      case "Prepaid": {
        const s = summary as ReviewPrepaidSummary;
        const showSecondSystemUtilityBills =
          hasSecondSystemUtilityBillComparison(s, isAdditionalSystem) &&
          !hideNonSolarSecondSystemComparison;
        return (
          <CategoryNoMargin>
            {!singleColumn && (
              <StyledHeader>
                <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                  Monthly payments
                </Typography>
              </StyledHeader>
            )}
            {showSecondSystemUtilityBills && (
              <SecondSystemWasNowComparison
                currentUtilityBill={s.averageEstimatedCurrentUtilityBill}
                newSystemUtilityBill={s.postSolarUtility}
                currentSunrunPayment={s.currentSunrunPayment}
                isMobile={isMobile}
                state={s.state}
              />
            )}
            {!showSecondSystemUtilityBills &&
              s.market === "Y1_MONTHLY_PAYMENTS" &&
              s.state !== "TX" &&
              !hideNonSolarSecondSystemComparison && (
                <>
                  <DividerRow>
                    <HelperText
                      size={14}
                      fontStyle="italic"
                      color={tokens.BRAND_HERO_BLUE}
                      isMobile={isMobile}
                    >
                      Was
                    </HelperText>
                    <InvisibleDivider />
                  </DividerRow>
                  <RowContainer
                    label="Estimated Monthly Avg. Pre-Solar Utility Bill"
                    cost={s.preSolarUtility}
                    unit="/mo"
                    accentColor={tokens.BRAND_HERO_BLUE}
                  />
                  <DividerRow>
                    <HelperText
                      size={14}
                      fontStyle="italic"
                      color={tokens.BRAND_HERO_BLUE}
                      isMobile={isMobile}
                    >
                      Now
                    </HelperText>
                    <FlexDivider />
                  </DividerRow>
                </>
              )}
            {!showSecondSystemUtilityBills &&
              !hideNonSolarSecondSystemComparison && (
                <RowContainerWithTooltip
                  tooltip={POST_SOLAR_UTILITY_BILL}
                  label="Avg. Est. Post-Solar Utility Bill"
                  cost={
                    s.market === "CONTROL" && s.state !== "TX"
                      ? "*"
                      : s.postSolarUtility
                  }
                  unit={s.market === "CONTROL" && s.state !== "TX" ? "" : "/mo"}
                  accentColor={tokens.BRAND_HERO_BLUE}
                />
              )}
            <MonthlyPricedEquipmentLineItems products={products} />
            {s.costcoCard > 0 && (
              <RowContainerWithTooltip
                tooltip={COSTCO_CARD_ESTIMATE}
                label="Est. Costco Shop Card"
                cost={s.costcoCard}
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            )}
          </CategoryNoMargin>
        );
      }
      case "Cash": {
        const s = summary as ReviewCashSummary;
        if (nonSolarView) return null;
        const showSecondSystemUtilityBills =
          hasSecondSystemUtilityBillComparison(s, isAdditionalSystem);
        return (
          <CategoryNoMargin>
            {!singleColumn && (
              <StyledHeader>
                <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
                  Monthly payments
                </Typography>
              </StyledHeader>
            )}
            {showSecondSystemUtilityBills && (
              <SecondSystemWasNowComparison
                currentUtilityBill={s.averageEstimatedCurrentUtilityBill}
                newSystemUtilityBill={s.postSolarUtility}
                currentSunrunPayment={s.currentSunrunPayment}
                isMobile={isMobile}
                state={s.state}
              />
            )}
            {!showSecondSystemUtilityBills &&
              s.market === "Y1_MONTHLY_PAYMENTS" &&
              s.state !== "TX" && (
                <>
                  <DividerRow>
                    <HelperText
                      size={14}
                      fontStyle="italic"
                      color={tokens.BRAND_HERO_BLUE}
                      isMobile={isMobile}
                    >
                      Was
                    </HelperText>
                    <InvisibleDivider />
                  </DividerRow>
                  <RowContainer
                    label="Estimated Monthly Avg. Pre-Solar Utility Bill"
                    cost={s.preSolarUtility}
                    unit="/mo"
                    accentColor={tokens.BRAND_HERO_BLUE}
                  />
                  <DividerRow>
                    <HelperText
                      size={14}
                      fontStyle="italic"
                      color={tokens.BRAND_HERO_BLUE}
                      isMobile={isMobile}
                    >
                      Now
                    </HelperText>
                    <FlexDivider />
                  </DividerRow>
                </>
              )}

            {!showSecondSystemUtilityBills && (
              <>
                <RowContainerWithTooltip
                  tooltip={POST_SOLAR_UTILITY_BILL}
                  label="Avg. Est. Post-Solar Utility Bill"
                  cost={
                    s.market === "CONTROL" && s.state !== "TX"
                      ? "*"
                      : s.postSolarUtility
                  }
                  unit={s.market === "CONTROL" && s.state !== "TX" ? "" : "/mo"}
                  accentColor={tokens.BRAND_HERO_BLUE}
                />
              </>
            )}
            <MonthlyPricedEquipmentLineItems products={products} />
            {s.costcoCard > 0 && (
              <RowContainerWithTooltip
                tooltip={COSTCO_CARD_ESTIMATE}
                label="Est. Costco Shop Card"
                cost={s.costcoCard}
                accentColor={tokens.BRAND_HERO_BLUE}
              />
            )}
          </CategoryNoMargin>
        );
      }
      default:
        return null;
    }
  };

  const showMonthlyPayments = !isNewHomes;
  const agreementDetails = renderAgreementDetails();
  const monthlyPayments = showMonthlyPayments ? renderMonthlyPayments() : null;

  if (!agreementDetails && !monthlyPayments) return null;

  if (singleColumn) {
    return (
      <PriceDetailsContainer singleColumn={true}>
        <SingleColumnWrapper isTablet={isTablet} isMobile={isMobile}>
          <Typography variant="h3" color={tokens.BRAND_HERO_BLUE}>
            Payment details
          </Typography>
          <div>
            {agreementDetails}
            {promotions && (
              <OfferReviewPromotionPills promotions={promotions} />
            )}
          </div>
          {monthlyPayments}
          <OfferMessages summary={summary} hasBattery={hasBattery} />
        </SingleColumnWrapper>
        {!isNewHomes && (
          <SimplifiedOfferReviewFootnote>
            Valid until &nbsp;
            {new Date(summary.expires).toLocaleDateString("en-US", {
              month: "numeric",
              day: "numeric",
              year: "numeric",
            })}
          </SimplifiedOfferReviewFootnote>
        )}
      </PriceDetailsContainer>
    );
  }

  return (
    <PriceDetailsContainer>
      <PriceDetailsColumns>
        <DetailsColumn>
          {agreementDetails}
          {promotions && <OfferReviewPromotionPills promotions={promotions} />}
        </DetailsColumn>
        {showMonthlyPayments && (
          <DividerWrapper>
            <Divider />
          </DividerWrapper>
        )}
        {showMonthlyPayments && (
          <DetailsColumn>
            {monthlyPayments}
            {!isNewHomes && (
              <SimplifiedOfferReviewFootnote>
                Valid until &nbsp;
                {new Date(summary.expires).toLocaleDateString("en-US", {
                  month: "numeric",
                  day: "numeric",
                  year: "numeric",
                })}
              </SimplifiedOfferReviewFootnote>
            )}
          </DetailsColumn>
        )}
      </PriceDetailsColumns>
      <OfferMessages summary={summary} hasBattery={hasBattery} />
    </PriceDetailsContainer>
  );
};

const NonSolarPaymentDetails = (props: NonSolarPaymentDetailsProps) => (
  <AgreementDetailsAndMonthlyPayments {...props} nonSolarView singleColumn />
);

const PriceDetailsContainer = styled.div<{ singleColumn?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ singleColumn }) =>
    singleColumn ? "flex-start" : "flex-end"};
  justify-content: center;
  gap: 16px;
  width: 100%;
`;

const PriceDetailsColumns = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: stretch;
  gap: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const DetailsColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const SingleColumnWrapper = styled.div<{
  isTablet: boolean;
  isMobile: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: ${({ isTablet, isMobile }) =>
    isTablet || isMobile ? "none" : "437px"};

  & > :empty {
    display: none;
  }
`;

const DividerWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: stretch;
  padding: 0px 48px 0px 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    padding: 0;
  }
`;

const Divider = styled.div`
  width: 1px;
  background-color: rgba(208, 204, 195, 0.8);

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    height: 1px;
  }
`;

const CategoryNoMargin = styled(StyledCategory)`
  margin: 0;
`;

const FlexDivider = styled(CardSectionDivider)`
  flex: 1;
  margin-top: 0;
  margin-bottom: 0;
`;

const InvisibleDivider = styled(CardSectionDivider)`
  margin-top: 0;
  margin-bottom: 0;
`;

const DividerRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  position: relative;
`;

const HelperText = styled(Typography)<{ isMobile: boolean }>`
  font-size: 14px;
  font-style: italic;
  color: ${tokens.BRAND_HERO_BLUE};
  position: ${({ isMobile }) => (isMobile ? "static" : "absolute")};
  left: -40px;
`;

const SimplifiedOfferReviewFootnote = styled(StyledFootnote)`
  color: ${tokens.TINTS_OFF_WHITE_20};
  margin-inline: 0;
`;

const MonthlyEquipmentNestedList = styled.ul`
  list-style: none;
  margin: 4px 0 12px;
  padding: 14px 16px 14px 38px;
  border-radius: 8px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    left: 22px;
    top: 10px;
    bottom: 31px;
    width: 0.7px;
    background: ${tokens.TINTS_OFF_WHITE_40};
  }
`;

const NestedEquipmentRow = styled.li`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 16px;
  padding: 7px 0 7px 4px;

  &::before {
    content: "";
    position: absolute;
    left: -16px;
    top: 50%;
    width: 15px;
    height: 1px;
    background: ${tokens.TINTS_OFF_WHITE_40};
    transform: translateY(-50%);
  }
`;

const NestedEquipmentLabel = styled.span`
  color: ${tokens.TINTS_OFF_WHITE_20};
`;

const NestedEquipmentPrice = styled.span`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.35;
  color: ${tokens.TINTS_OFF_WHITE_20};
  text-align: right;
  flex-shrink: 0;
`;

export { AgreementDetailsAndMonthlyPayments, NonSolarPaymentDetails };
